import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../redux/slices/authSlice';
import { logout as clientLogout } from '../redux/slices/clientAuthSlice';
import { AppDispatch, RootState } from '../redux/store';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  MoneyOff as MoneyOffIcon,
  Receipt as ReceiptIcon,
  Logout as LogoutIcon,
  Visibility as VisibilityIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  AccountCircle as AccountCircleIcon,
  Translate as TranslateIcon,
  PhoneAndroid as PhoneAndroidIcon,
} from '@mui/icons-material';

interface LayoutProps {
  children: React.ReactNode;
}

// Menu items pour les admins
const adminMenuItems = [
  { title: 'Tableau de bord', path: '/', icon: <DashboardIcon /> },
  { title: 'Clients', path: '/clients', icon: <PeopleIcon /> },
  { title: 'Démarches', path: '/demarches', icon: <DescriptionIcon /> },
  { title: 'Monitoring ANEF', path: '/monitoring', icon: <VisibilityIcon /> },
  { title: 'Dossiers à revoir', path: '/dossiers-a-revoir', icon: <AssignmentIcon /> },
  { title: 'Interventions', path: '/interventions-requises', icon: <WarningIcon /> },
  { title: 'Impayés', path: '/impayees', icon: <MoneyOffIcon /> },
  { title: 'Factures', path: '/factures', icon: <ReceiptIcon /> },
  { title: 'Utilisateurs Cyber', path: '/cyber-users', icon: <AdminPanelSettingsIcon /> },
  { title: 'Traductions', path: '/translations', icon: <TranslateIcon /> },
];

// Menu items pour les utilisateurs cyber
const cyberMenuItems = [
  { title: 'Tableau de bord Cyber', path: '/cyber-dashboard', icon: <DashboardIcon /> },
  { title: 'Mes Clients', path: '/clients', icon: <PeopleIcon /> },
  { title: 'Démarches', path: '/demarches', icon: <DescriptionIcon /> },
  { title: 'Monitoring ANEF', path: '/monitoring', icon: <VisibilityIcon /> },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { user: clientUser, isAuthenticated: isClientAuth } = useSelector((state: RootState) => state.clientAuth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  // Déterminer l'utilisateur actuel (auth classique ou clientAuth)
  const currentUser = user || clientUser;
  const userRole = currentUser?.role || (clientUser as any)?.user_type;

  // Sélectionner les items de menu selon le rôle
  const menuItems = userRole === 'cyber' ? cyberMenuItems : adminMenuItems;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    // Déconnecter selon le type d'authentification
    if (isClientAuth) {
      dispatch(clientLogout());
      navigate('/client/login');
    } else {
      dispatch(logout());
      navigate('/login');
    }
    handleCloseUserMenu();
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, fontWeight: 700, color: 'primary.main' }}>
        Gestion Multi-Service
      </Typography>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.title} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Mobile menu icon */}
            <IconButton
              color="primary"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontWeight: 700,
                color: 'primary.main',
                textDecoration: 'none',
              }}
            >
              Gestion Multi-Service
            </Typography>

            {/* Mobile Logo */}
            <Typography
              variant="h6"
              noWrap
              component={Link}
              to="/"
              sx={{
                flexGrow: 1,
                display: { xs: 'flex', md: 'none' },
                fontWeight: 700,
                color: 'primary.main',
                textDecoration: 'none',
              }}
            >
              GMS
            </Typography>

            {/* Desktop Menu */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1, ml: 3 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    position: 'relative',
                    '&::after': location.pathname === item.path ? {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '80%',
                      height: '3px',
                      backgroundColor: 'primary.main',
                      borderRadius: '3px 3px 0 0',
                    } : {},
                  }}
                >
                  {item.title}
                </Button>
              ))}
            </Box>

            {/* User Menu */}
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Paramètres du compte">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    {currentUser?.prenom?.[0]}{currentUser?.nom?.[0]}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem disabled>
                  <Typography textAlign="center">
                    {currentUser?.prenom} {currentUser?.nom}
                  </Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography textAlign="center" variant="caption" color="text.secondary">
                    {currentUser?.email}
                  </Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography textAlign="center" variant="caption" color="primary.main" fontWeight={600}>
                    {userRole === 'admin' ? 'Administrateur' : 'Cyber'}
                  </Typography>
                </MenuItem>
                <Divider />
                {userRole === 'cyber' && (
                  <MenuItem onClick={() => { navigate('/cyber-profile'); handleCloseUserMenu(); }}>
                    <ListItemIcon>
                      <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    Mon Profil
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Déconnexion
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: 8,
          width: '100%',
        }}
      >
        <Container maxWidth="xl">
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
